// 開發進度：已完成 Label 樣式的 Large，Medium 與 Small 則完成基本設定

// 屬性說明
// clearable：是否顯示清除按鈕

import _ from 'lodash'
import React, { useState } from 'react'
import MuiTextField from '@mui/material/TextField'
import styled from '@emotion/styled'
import InputAdornment from '@mui/material/InputAdornment'
import { ClearSVG } from 'components/common/SVGElements'
import Box from '@mui/material/Box'

// 依 size 取得輸入框整體&外框樣式
const getOutlinedAndInputStyles = (size, theme) => {
  switch (size) {
    case 'large':
      return `
        font-size: ${theme.typography.bodyLarge.fontSize};
        height: 56px;
        padding: 16px;
        gap: 8px;
        svg {
          width: 24px;
          height: 24px;
        }
      `
    case 'medium':
      return `
        font-size: ${theme.typography.bodyMedium.fontSize};
        height: 44px;
        padding: 12px;
        gap: 6px;
        svg {
          width: 20px;
          height: 20px;
        }
      `
    case 'small':
      return `
        font-size: ${theme.typography.bodySmall.fontSize};
        height: 37px;
        padding: 10px;
        gap: 4px;
        svg {
          width: 16px;
          height: 16px;
        }
      `
    default:
      return ''
  }
}

// 依 size 取得輸入框基本內容樣式
// height 算法為該元件高度扣除 padding，與 Figma 相符
const getInputBaseStyles = (size, theme, isStartAdornment) => {
  switch (size) {
    case 'large':
      return `
        font-size: ${theme.typography.bodyLarge.fontSize};
        height: 24px;
        padding: 16px 16px 16px ${isStartAdornment ? '0px' : '16px'};
        &::placeholder {
          font-size: ${theme.typography.bodyLarge.fontSize};
          font-weight: ${theme.typography.bodyLarge.fontWeight};
        }
      `
    case 'medium':
      return `
        font-size: ${theme.typography.bodyMedium.fontSize};
        height: 20px;
        padding: 12px 12px 12px ${isStartAdornment ? '0px' : '12px'};
        &::placeholder {
          font-size: ${theme.typography.bodyMedium.fontSize};
          font-weight: ${theme.typography.bodyMedium.fontWeight};
        }
      `
    case 'small':
      return `
        font-size: ${theme.typography.bodySmall.fontSize};
        height: 17px;
        padding: 10px 10px 10px ${isStartAdornment ? '0px' : '10px'};
        &::placeholder {
          font-size: ${theme.typography.bodySmall.fontSize};
          font-weight: ${theme.typography.bodySmall.fontWeight};
        }
      `
    default:
      return ''
  }
}

// 依 size 取得標籤樣式
// 優化：目前手動調整 line-height 達成垂直置中，可優化
const getInputLabelStyles = (size, theme) => {
  switch (size) {
    case 'large':
      return `
        font-size: ${theme.typography.bodyLarge.fontSize};
        line-height: 22px;
      `
    case 'medium':
      return `
        font-size: ${theme.typography.bodyMedium.fontSize};
        line-height: 15px;
      `
    case 'small':
      return `
        font-size: ${theme.typography.bodySmall.fontSize};
        line-height: 19px;
      `
    default:
      return ''
  }
}

// 依 size 取得 helper text 樣式
const getHelperTextStyles = (size, theme) => {
  switch (size) {
    case 'large':
      return `
        font-size: ${theme.typography.paraBodySmall.fontSize};
        font-weight: ${theme.typography.paraBodySmall.fontWeight};
        line-height: ${theme.typography.paraBodySmall.lineHeight};
        letter-spacing: ${theme.typography.paraBodySmall.letterSpacing};
        margin: 4px 16px 0px;
      `
    case 'medium':
    case 'small':
      return `
        font-size: ${theme.typography.paraBodyXs.fontSize};
        font-weight: ${theme.typography.paraBodyXs.fontWeight};
        line-height: ${theme.typography.paraBodyXs.lineHeight};
        letter-spacing: ${theme.typography.paraBodyXs.letterSpacing};
        margin: 2px 12px 0px;
      `
    default:
      return ''
  }
}

const StyledTextField = styled(MuiTextField)`
  ${({
    theme,
    direction,
    size,
    error,
    isDisabledAndHasValue,
    isLabelTextGreen,
    isStartAdornment,
  }) => `
    .MuiOutlinedInput-root,
    .MuiInputBase-root {
      ${getOutlinedAndInputStyles(size, theme)}
    }
  
    .MuiInputBase-input {
      color: ${theme.palette.primary.main};
      text-align: ${direction || 'inherit'};
      ${getInputBaseStyles(size, theme, isStartAdornment)}
      &::placeholder {
        opacity: 1;
        color: ${theme.palette.text.secondary};
      }
    }

    .MuiInputLabel-root, fieldset {
      ${getInputLabelStyles(size, theme)}
      border-color: ${theme.palette.background.line};
      color: ${
        error
          ? theme.palette.error.main
          : isLabelTextGreen
          ? theme.palette.primary.main
          : theme.palette.text.secondary
      }
    }

    .MuiFormHelperText-root {
      ${getHelperTextStyles(size, theme)}
      color: ${error ? theme.palette.error.main : theme.palette.text.secondary}
    }

    // disabled 狀態下的樣式覆寫
    .MuiOutlinedInput-root.Mui-disabled {
      background-color: ${
        isDisabledAndHasValue ? 'inherit' : theme.palette.background.gray
      };
      .MuiOutlinedInput-notchedOutline {
        border-color: ${
          isDisabledAndHasValue
            ? theme.palette.text.minus2
            : theme.palette.background.line
        };
      }
    }
    .MuiInputBase-input.Mui-disabled {
      background-color: ${
        isDisabledAndHasValue ? 'inherit' : theme.palette.background.gray
      };
      color: ${isDisabledAndHasValue ? 'inherit' : theme.palette.text.minus2};
    }
    .MuiInputLabel-root.Mui-disabled {
      color: ${
        isDisabledAndHasValue
          ? theme.palette.text.minus2
          : theme.palette.text.disabled
      };
    }
    .MuiOutlinedInput-input.Mui-disabled {
      -webkit-text-fill-color: ${
        isDisabledAndHasValue
          ? theme.palette.text.minus2
          : theme.palette.text.disabled
      };
    }

    #clear-svg {
      width: 16px;
      height: 16px;
    }

    // 自動填入時透過延遲背景色變化來防止瀏覽器預設淺藍底色出現
    input:-webkit-autofill {
      -webkit-box-shadow: 0 0 0 100px transparent inset;
      -webkit-text-fill-color: ${theme.palette.primary.main};
      transition: background-color 5000s ease-in-out 0s;
    }

  `}
`

const TextFieldBasic = (props) => {
  const {
    InputProps,
    onFocus,
    onBlur,
    onChange,
    setErrorState,
    value,
    clearable = false,
    disabled = false,
    error = false,
    size = 'large',
    ...rest
  } = props

  const [isFocused, setIsFocused] = useState(false)
  const isDisabledAndHasValue = disabled && value !== ''
  const isStartAdornment = !!_.get(InputProps, 'startAdornment')
  const isLabelTextGreen = !error && isFocused

  const handleFocus = (e) => {
    if (onFocus) onFocus(e)
    setIsFocused(true)
  }

  const handleBlur = (e) => {
    if (onBlur) onBlur(e)
    setIsFocused(false)
  }

  const handleChange = (e) => {
    if (onChange) onChange(e.target.value)
    if (setErrorState && error) {
      setErrorState(false)
    }
  }

  const handleClear = () => {
    if (onChange) {
      onChange('')
    }
  }

  // 將外部傳入的 InputProps 與清除按鈕做合併
  const mergedInputProps = {
    ...InputProps,
    // 若外部有設定 endAdornment 則亦保留原有元素
    endAdornment: (
      <>
        {InputProps && InputProps.endAdornment}
        {clearable && !disabled && (
          <InputAdornment position="end">
            {value && isFocused ? (
              <Box
                display="flex"
                alignItems="center"
                p={1}
                onClick={handleClear}
                onMouseDown={(e) => e.preventDefault()}
                sx={{ cursor: 'pointer' }}
              >
                <ClearSVG id="clear-svg" />
              </Box>
            ) : (
              <Box display="flex" alignItems="center">
                <Box height={24} width={24}></Box>
              </Box>
            )}
          </InputAdornment>
        )}
      </>
    ),
  }

  return (
    <StyledTextField
      {...rest}
      size={size}
      value={value}
      disabled={disabled}
      error={error}
      isFocused={isFocused}
      isDisabledAndHasValue={isDisabledAndHasValue}
      isStartAdornment={isStartAdornment}
      isLabelTextGreen={isLabelTextGreen}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      InputProps={mergedInputProps}
    />
  )
}

export default TextFieldBasic
